/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { SubOfficeStatus } from '@gh/builder-shared-data';
import {
  rendererIconByName,
  ROW_DELETED_CLASS,
  ROW_IN_DELETION_CLASS,
  truthyCellRenderer,
} from '@gh/common-internal-shared';
import { LayoutBasedGridDefinition } from '@gh/core-ui-grid';

import { SubOfficeListItem } from '../../resources/sub-office-list-item.model';

const STATUSES = {
  'NEW': 'New',
  'ACTIVE': 'Active',
  'BLOCKED': 'Blocked',
  'DEACTIVATED': 'Deactivated',
  'IN_DELETION': 'In Deletion',
};

const STATUS_CLASS_BY_VALUE: Record<string, string> = {
  [SubOfficeStatus.New]: 'new-status',
  [SubOfficeStatus.Active]: 'active-status',
  [SubOfficeStatus.Blocked]: 'blocked-status',
  [SubOfficeStatus.Deactivated]: 'deactivated-status',
  [SubOfficeStatus.InDeletion]: 'inDeletion-status',
};
const STATUS_KEYS = Object.keys(STATUSES);

function normalizeStatusKey(value: any): string {
  if (!value) {
    return '';
  }

  if (typeof value === 'object') {
    const raw = value.value || value.id || value.name || value.status || value.label || value.text || '';
    return normalizeStatusKey(raw);
  }

  const text = String(value).trim();
  if (!text || text === '-') {
    return '';
  }

  return text.toUpperCase().replace(/\s+/g, '_');
}

function statusKeyFromCandidate(candidate: any): string {
  const direct = normalizeStatusKey(candidate);
  if (STATUS_KEYS.includes(direct)) {
    return direct;
  }

  const asText = String(candidate || '').toUpperCase();
  if (!asText) {
    return '';
  }

  if (asText.includes('IN_DELETION') || asText.includes('IN DELETION')) {
    return SubOfficeStatus.InDeletion;
  }
  if (asText.includes('DEACTIVATED')) {
    return SubOfficeStatus.Deactivated;
  }
  if (asText.includes('BLOCKED')) {
    return SubOfficeStatus.Blocked;
  }
  if (asText.includes('ACTIVE')) {
    return SubOfficeStatus.Active;
  }
  if (asText.includes('NEW')) {
    return SubOfficeStatus.New;
  }
  return '';
}

function resolveStatusKey(data: any): string {
  return statusKeyFromCandidate(data?.subOfficeStatus)
    || statusKeyFromCandidate(data?.statusValue)
    || statusKeyFromCandidate(data?.status);
}

function resolveStatusLabel(data: any, value: any): string {
  const key = normalizeStatusKey(value) || resolveStatusKey(data);
  const labelFromKey = STATUSES[key];
  const rawLabel = data?.status && data.status !== '-' ? String(data.status) : '';
  return (labelFromKey || rawLabel || '').trim();
}

@Injectable()
export class SubOfficeListGridDefinition extends LayoutBasedGridDefinition {
  private readonly statusColumn = {
    minWidth: 105,
    valueFormatter: (params: any) => {
      const data = params?.data || params?.node?.data;
      return resolveStatusLabel(data, params?.value);
    },
    cellClass: ({ value, data }: { value: any; data: any }) =>
      STATUS_CLASS_BY_VALUE[normalizeStatusKey(value) || resolveStatusKey(data)] || '',
    cellClassRules: {
      'new-status': ({ value, data }: { value: any; data: any }) =>
        (normalizeStatusKey(value) || resolveStatusKey(data)) === SubOfficeStatus.New
          || resolveStatusLabel(data, value).toUpperCase() === 'NEW',
      'active-status': ({ value, data }: { value: any; data: any }) =>
        (normalizeStatusKey(value) || resolveStatusKey(data)) === SubOfficeStatus.Active
          || resolveStatusLabel(data, value).toUpperCase() === 'ACTIVE',
      'blocked-status': ({ value, data }: { value: any; data: any }) =>
        (normalizeStatusKey(value) || resolveStatusKey(data)) === SubOfficeStatus.Blocked
          || resolveStatusLabel(data, value).toUpperCase() === 'BLOCKED',
      'deactivated-status': ({ value, data }: { value: any; data: any }) =>
        (normalizeStatusKey(value) || resolveStatusKey(data)) === SubOfficeStatus.Deactivated
          || resolveStatusLabel(data, value).toUpperCase() === 'DEACTIVATED',
      'inDeletion-status': ({ value, data }: { value: any; data: any }) =>
        (normalizeStatusKey(value) || resolveStatusKey(data)) === SubOfficeStatus.InDeletion
          || resolveStatusLabel(data, value).toUpperCase() === 'IN DELETION',
    },
  };

  columnDefs = {
    subOfficeStatus: {
      ...this.statusColumn,
      field: 'subOfficeStatus',
    },
    status: {
      ...this.statusColumn,
      field: 'status',
    },
    isPublicListed: {
      cellRenderer: truthyCellRenderer(rendererIconByName('done')),
    },
  };

  options = {
    rowClassRules: {
      [ROW_DELETED_CLASS]: ({ data }: { data: SubOfficeListItem }) =>
        resolveStatusKey(data) === SubOfficeStatus.Deactivated,
      'deleted-class': ({ data }: { data: SubOfficeListItem }) =>
        resolveStatusKey(data) === SubOfficeStatus.Deactivated,
      [ROW_IN_DELETION_CLASS]: ({ data }: { data: SubOfficeListItem }) =>
        resolveStatusKey(data) === SubOfficeStatus.InDeletion,
    },
    getRowId: (params) => String(params.data.subOfficeId),
  };

  constructor() {
    super();
  }
}
